<script lang="ts">
import { writable } from 'svelte/store';

import { Button, Icon, IconButton, TextInput } from '@viamrobotics/prime-core';
import type * as APITypes from '@viamrobotics/web-client/app/v1/app';

import { Link } from '$lib/components/link';
import { compareAlphaNum } from '$lib/sort';

import CreateOrgForm from './create-org-form.svelte';
import Menu from './menu.svelte';

export let currentOrg: APITypes.Organization | undefined;
export let recentOrgs: APITypes.Organization[] | undefined;
export let allOrgs: APITypes.Organization[];
export let showTryButton = false;
export let onChangeOrg: (orgID: string) => void;
export let onCreateOrg: (name: string, onSuccess: () => void) => void;

type CreateOrgFormSection = 'recent' | 'all';

const isOpen = writable(false);
let createOrgFormSection: CreateOrgFormSection | null = null;
let createOrgName = '';

const closeMenu = () => isOpen.set(false);

const toggleCreateOrgForm = (section: CreateOrgFormSection) => {
  createOrgFormSection = createOrgFormSection === section ? null : section;
};

$: {
  if (!$isOpen) {
    createOrgFormSection = null;
  }
}

$: {
  if (createOrgFormSection === null) {
    createOrgName = '';
  }
}

const handleChangeCreateOrgName = (event: Event) => {
  createOrgName = (event.currentTarget as HTMLInputElement).value.trim();
};

const handleChangeOrg = (id: string) => {
  closeMenu();
  onChangeOrg(id);
};

const handleSubmitCreateOrg = () => {
  onCreateOrg(createOrgName, closeMenu);
};

$: currentOrgName = currentOrg ? currentOrg.name : '';
$: sortedRecentOrgs = recentOrgs
  ? [...recentOrgs].sort((a, b) => compareAlphaNum(a.name, b.name))
  : undefined;
$: sortedOrgs = [...allOrgs].sort((a, b) => compareAlphaNum(a.name, b.name));
</script>

{#if currentOrg}
  <Menu
    {isOpen}
    label="Current organization"
    showIndicator
  >
    <svelte:fragment slot="button-text">
      {#if currentOrg.name === ''}
        <Icon name="logout" />
      {:else}
        {currentOrgName}
      {/if}
    </svelte:fragment>
    <h2
      class="border-b border-b-light p-4 text-base font-semibold leading-none"
    >
      {currentOrgName}
    </h2>
    <nav
      class="flex flex-col border-b border-b-light py-2 text-sm text-subtle-1"
    >
      <Link
        href={`/organization/${currentOrg.id}/settings`}
        class="flex items-center gap-3 px-4 py-1.5 hover:bg-medium"
        onclick={closeMenu}
      >
        <div class="text-gray-6">
          <Icon name="cog" />
        </div>
        Settings
      </Link>
      <Link
        href={`/organization/${currentOrg.id}/billing`}
        class="flex items-center gap-3 px-4 py-1.5 hover:bg-medium"
        onclick={closeMenu}
      >
        <div class="text-gray-6">
          <Icon name="credit-card-outline" />
        </div>
        Payment & Billing
      </Link>
      <Link
        href={`/organization/${currentOrg.id}/members`}
        class="flex items-center gap-3 px-4 py-1.5 hover:bg-medium"
        onclick={closeMenu}
      >
        <div class="text-gray-6">
          <Icon name="account-plus-outline" />
        </div>
        Members & Invites
      </Link>
    </nav>
    {#if showTryButton}
      <nav
        class="flex flex-col border-b border-b-light py-2 text-sm text-subtle-1"
      >
        <Link
          href="/try"
          class="flex items-center gap-3 px-4 py-1.5 hover:bg-medium"
          onclick={closeMenu}
        >
          <div class="text-gray-6">
            <Icon name="robot-outline" />
          </div>
          Try Viam
        </Link>
      </nav>
    {/if}
    {#if sortedRecentOrgs}
      <nav class="flex flex-col border-b border-b-light text-sm text-subtle-1">
        <div class="mt-4 flex items-center justify-between px-4">
          <h3
            class="flex font-roboto-mono text-xs uppercase leading-6 tracking-wider text-subtle-2"
          >
            Recent organizations
          </h3>
          <IconButton
            icon="plus"
            label="create organization"
            cx="text-subtle-2"
            on:click={() => toggleCreateOrgForm('recent')}
          />
        </div>
        {#if createOrgFormSection === 'recent'}
          <CreateOrgForm
            value={createOrgName}
            onInput={handleChangeCreateOrgName}
            onSubmit={handleSubmitCreateOrg}
          />
        {/if}
        <ul
          class="py-2"
          aria-label="recent-organizations"
        >
          {#each sortedRecentOrgs as org (org.id)}
            <li>
              <button
                class="w-full px-4 py-1.5 text-left hover:bg-medium"
                on:click={() => handleChangeOrg(org.id)}
              >
                {org.name}
              </button>
            </li>
          {/each}
        </ul>
      </nav>
    {/if}
    <section>
      <div class="mt-4 flex items-center justify-between px-4">
        <h3
          class="flex font-roboto-mono text-xs uppercase leading-6 tracking-wider text-subtle-2"
        >
          All organizations
        </h3>
        <IconButton
          icon="plus"
          label="create organization"
          cx="text-subtle-2"
          on:click={() => toggleCreateOrgForm('all')}
        />
      </div>
      {#if createOrgFormSection === 'all'}
        <CreateOrgForm
          value={createOrgName}
          onInput={handleChangeCreateOrgName}
          onSubmit={handleSubmitCreateOrg}
        />
      {/if}
      <ul
        class="py-2"
        aria-label="other-organizations"
      >
        {#each sortedOrgs as org (org.id)}
          <li>
            <button
              class="w-full px-4 py-1.5 text-left hover:bg-medium"
              on:click={() => handleChangeOrg(org.id)}
            >
              {org.name}
            </button>
          </li>
        {/each}
      </ul>
    </section>
  </Menu>
{:else}
  <Menu
    {isOpen}
    label="Create organization"
    showIndicator
  >
    <svelte:fragment slot="button-text">Create organization</svelte:fragment>
    <section class="p-4">
      <form
        on:submit|preventDefault={handleSubmitCreateOrg}
        aria-label="Create organization"
        class="flex items-end gap-1"
      >
        <TextInput
          aria-label="Organization name"
          value={createOrgName}
          placeholder="New organization"
          on:input={handleChangeCreateOrgName}
        />
        <Button
          variant="dark"
          type="submit"
        >
          Create
        </Button>
      </form>
    </section>
  </Menu>
{/if}
